/**
 * Image Element Editor
 * Uses MediaEdit for src field, FormBuilder for other fields
 */

import * as React from 'react';
import { cn, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Input } from '@ai-content-hub/content-kit';
import type { ElementEditorProps } from '@ai-content-hub/content-kit';
import { MediaEdit } from '../media';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';
import { imageElementSchema, type ImageElementData } from './schema';

export type ImageEditHiddenField = 'alt' | 'caption' | 'size' | 'alignment';

export interface ImageEditConfig {
    hiddenFields?: ImageEditHiddenField[];
    defaults?: Partial<Pick<ImageElementData, 'size' | 'alignment'>>;
    borderless?: boolean;
}

export interface ImageEditProps extends ElementEditorProps<ImageElementData> {
    imageConfig?: ImageEditConfig;
}

export const ImageEdit: React.FC<ImageEditProps> = ({
    value,
    onChange,
    errors,
    disabled,
    className,
    imageConfig,
    config,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? imageElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    const hiddenFields = imageConfig?.hiddenFields ?? [];
    const isHidden = (field: ImageEditHiddenField) => hiddenFields.includes(field);
    const borderless = imageConfig?.borderless ?? false;

    React.useEffect(() => {
        if (!imageConfig?.defaults) return;
        const updates: Partial<ImageElementData> = {};
        if (imageConfig.defaults.size && !value.size) updates.size = imageConfig.defaults.size;
        if (imageConfig.defaults.alignment && !value.alignment) updates.alignment = imageConfig.defaults.alignment;
        if (Object.keys(updates).length > 0) onChange({ ...value, ...updates });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Handle src change from MediaEdit
    const handleSrcChange = React.useCallback(
        (mediaData: { src: string; mediaType: string; title?: string }) => {
            onChange({
                ...value,
                src: mediaData.src,
                // Use media title as alt if alt is empty
                alt: value.alt || mediaData.title || '',
            });
        },
        [value, onChange]
    );

    // Handle field change
    const handleFieldChange = React.useCallback(
        <K extends keyof ImageElementData>(field: K, fieldValue: ImageElementData[K]) => {
            onChange({
                ...value,
                [field]: fieldValue,
            });
        },
        [value, onChange]
    );

    return (
        <div className={cn('image-edit space-y-6', borderless ? '' : 'p-0', className)}>
            {/* Image Source Section */}
            <div className="space-y-3">
                {!borderless && <Label className="text-sm font-medium">{t('image')}</Label>}
                <MediaEdit
                    value={{
                        src: value.src || '',
                        mediaType: 'image',
                        title: value.alt || '',
                    }}
                    onChange={handleSrcChange}
                    mediaType="image"
                    layout="default"
                    disabled={disabled}
                    config={config}
                    language={language}
                    displayConfig={{
                        fit: 'contain',
                        maxHeight: 200,
                    }}
                />
                {errors?.src && (
                    <p className="text-sm text-destructive">{errors.src}</p>
                )}
            </div>

            {/* Alt Text */}
            {!isHidden('alt') && (
                <div className="space-y-2">
                    <Label htmlFor="alt" className="text-sm font-medium">
                        {t('altText')} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                        id="alt"
                        value={value.alt || ''}
                        onChange={(e) => handleFieldChange('alt', e.target.value)}
                        placeholder={t('describeImage')}
                        disabled={disabled}
                    />
                    {errors?.alt && (
                        <p className="text-sm text-destructive">{errors.alt}</p>
                    )}
                </div>
            )}

            {/* Caption */}
            {!isHidden('caption') && (
                <div className="space-y-2">
                    <Label htmlFor="caption" className="text-sm font-medium">
                        {t('caption')}
                    </Label>
                    <Input
                        id="caption"
                        value={value.caption || ''}
                        onChange={(e) => handleFieldChange('caption', e.target.value)}
                        placeholder={t('imageCaptionPlaceholder')}
                        disabled={disabled}
                    />
                </div>
            )}

            {/* Size and Alignment */}
            {(!isHidden('size') || !isHidden('alignment')) && (
                <div className="grid grid-cols-2 gap-4">
                    {!isHidden('size') && (
                        <div className="space-y-2">
                            <Label htmlFor="size" className="text-sm font-medium">
                                {t('size')}
                            </Label>
                            <Select
                                value={value.size || imageConfig?.defaults?.size || 'medium'}
                                onValueChange={(v) => handleFieldChange('size', v as ImageElementData['size'])}
                                disabled={disabled}
                            >
                                <SelectTrigger id="size">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="small">{t('sizeSmall')}</SelectItem>
                                    <SelectItem value="medium">{t('sizeMedium')}</SelectItem>
                                    <SelectItem value="large">{t('sizeLarge')}</SelectItem>
                                    <SelectItem value="full">{t('sizeFull')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {!isHidden('alignment') && (
                        <div className="space-y-2">
                            <Label htmlFor="alignment" className="text-sm font-medium">
                                {t('alignment')}
                            </Label>
                            <Select
                                value={value.alignment || imageConfig?.defaults?.alignment || 'center'}
                                onValueChange={(v) => handleFieldChange('alignment', v as ImageElementData['alignment'])}
                                disabled={disabled}
                            >
                                <SelectTrigger id="alignment">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="left">{t('alignLeft')}</SelectItem>
                                    <SelectItem value="center">{t('alignCenter')}</SelectItem>
                                    <SelectItem value="right">{t('alignRight')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ImageEdit;
